import React, { useState, useEffect, useCallback } from 'react';
import { Form, Select, DatePicker, Button, Table, Spin, Typography, Image, Card, Modal } from 'antd';
import { SearchOutlined, BookOutlined, DollarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import api from '../../../services/api';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { Dirent } from 'fs';

const { Title, Text } = Typography;
const { Option } = Select;

// Tipos para os dados
interface Port {
  _id: string;
  port_name: string;
  port_code: string;
  country: string;
}

interface FormData {
  originCode: string;
  destinationCode: string;
  fromDate: moment.Moment | null;
}

interface Schedule {
  carrier: string;
  service: string;
  vessel: string;
  voy: string;
  etd: string;
  eta: string;

  transitTime: string;
  cutOffs?: any; // Armazena os dados brutos de cut-offs para exibição no modal
}

// Logos dos carriers
const CARRIER_LOGOS = {
  MSC: "/imagens/armadores/Msc.png",
  CMA: "/imagens/armadores/CmaCgm.png",
  ONE: "/imagens/armadores/One.png",
  HAPAG: "/imagens/armadores/Hapag.png",
  MAERSK: "/imagens/armadores/Maersk.png", // Adicione o logo da Maersk
};

// Variantes para animação com Framer Motion
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fadeInUpDelayed = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.3 } },
};



const getSiglaFullName = (sigla) => {
  const siglaMap = {
    // Grupo 1
    'ANID': 'Arrival Notice Issue Date',
    'SINONAMS': 'Shipping Instructions and Nomination of Agents',
    'SGIN': 'Shipping Instructions',
    'VGM': 'Verified Gross Mass',
    
    // Grupo 2
    'SPECIALBOOKINGCUTOFF': 'Special Booking Cut-off',
    'STANDARDBOOKINGCUTOFF': 'Standard Booking Cut-off',
    'SICUTOFF': 'Shipping Instructions Cut-off',
    'VGMCUTOFF': 'VGM Cut-off',
    'PORTCUTOFF': 'Port Cut-off',
    
    // Grupo 3
    'CONTAINERYARDCUTOFFDATE': 'Container Yard Cut-off Date',
    'REEFERCUTOFFDATE': 'Reefer Cut-off Date',
    'DANGEROUSCARGOCUTOFFDATE': 'Dangerous Cargo Cut-off Date',
    'SHIPPINGINSTRUCTIONSCUTOFFDATE': 'Shipping Instructions Cut-off Date',
    'VERIFIEDGROSSMASSCUTOFFDATE': 'Verified Gross Mass Cut-off Date',
    
    // Grupo 4
    'CCT': 'Container Cut-off Time',
    'DCT': 'Documentation Cut-off Time',
    'VGMCCT': 'VGM Container Cut-off Time',
    'INLANDCCT': 'Inland Container Cut-off Time',
    
    // Grupo 5
    'FCO': 'Full Container Cut-off',
    'DGD': 'Dangerous Goods Declaration',
    'LCO': 'Less Container Load Cut-off',
    'OOG': 'Out of Gauge',
    'REF': 'Reefer',
    'SHI': 'Shipping Instructions',
    'VCO': 'VGM Cut-off',
    'DOC': 'Documentation',
    'DBC': 'Delivery Before Cut-off',
    
    // Novas siglas
    'CDCO': 'Container Delivery Cut-off',
    'DGDSDS': 'Dangerous Goods Declaration Submission Deadline',
    'ECPL': 'Export Cargo Packing List',
    
    // Siglas adicionais comuns
    'ETD': 'Estimated Time of Departure',
    'ETA': 'Estimated Time of Arrival',
    'ATD': 'Actual Time of Departure',
    'ATA': 'Actual Time of Arrival',
    'CFS': 'Container Freight Station',
    'CY': 'Container Yard',
    'FCL': 'Full Container Load',
    'LCL': 'Less than Container Load',
    'THC': 'Terminal Handling Charge',
    'BAF': 'Bunker Adjustment Factor',
    'CAF': 'Currency Adjustment Factor',
    'PSS': 'Peak Season Surcharge',
    'GRI': 'General Rate Increase',
    'LSS': 'Low Sulphur Surcharge',
    'EBS': 'Emergency Bunker Surcharge',
    'IMO': 'International Maritime Organization',
    'ISPS': 'International Ship and Port Facility Security',
    'CSC': 'Container Safety Convention',
    'SOLAS': 'Safety of Life at Sea',
    'MARPOL': 'Marine Pollution Prevention',
    'BL': 'Bill of Lading',
    'AWB': 'Air Waybill',
    'HBL': 'House Bill of Lading',
    'MBL': 'Master Bill of Lading',
    'SWB': 'Sea Waybill',
    'CO': 'Certificate of Origin',
    'CI': 'Commercial Invoice',
    'PL': 'Packing List',
    'LC': 'Letter of Credit',
    'DO': 'Delivery Order',
    'POD': 'Port of Discharge',
    'POL': 'Port of Loading',
    'POO': 'Port of Origin',
    'GATE': 'Gate Cut-off',
    'RAIL': 'Rail Cut-off',
    'BARGE': 'Barge Cut-off',
    'TRUCK': 'Truck Cut-off',
    'CUSTOMS': 'Customs Cut-off',
    'MANIFEST': 'Manifest Cut-off',
    'BOOKING': 'Booking Cut-off',
    'AMENDMENT': 'Amendment Cut-off',
    'CANCELLATION': 'Cancellation Cut-off',
    'EQUIPMENT': 'Equipment Cut-off',
    'EMPTY': 'Empty Return Cut-off',
    'LADEN': 'Laden Cut-off',
    'EXPORT': 'Export Cut-off',
    'IMPORT': 'Import Cut-off',
    'TRANSHIPMENT': 'Transhipment Cut-off',
    'FEEDER': 'Feeder Cut-off',
    'MOTHER': 'Mother Vessel Cut-off',
    'CONNECTING': 'Connecting Service Cut-off'
  };
  
  return siglaMap[sigla.toUpperCase()] || sigla;
};




// Ativar os plugins necessários
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

const formatCutOffDate = (dateString) => {
  if (!dateString) return '';
  
  // Remover espaços extras e normalizar
  const cleanDate = dateString.trim();
  
  // Diferentes formatos possíveis
  const formats = [
    // 17 Jun 2025 19:00
    'DD MMM YYYY HH:mm',
    // 2025-06-18 12:00
    'YYYY-MM-DD HH:mm',
    // Wed 18th Jun 2025 12:00
    'ddd Do MMM YYYY HH:mm',
    // Sun 29th Jun 2025 12:00
    'ddd Do MMM YYYY HH:mm',
    // 16-JUN 2025, 11:30 AM
    'DD-MMM YYYY, hh:mm A',
    // 17-JUN 2025, 12:00 PM
    'DD-MMM YYYY, hh:mm A',
    // Sat 14th Jun 2025 18:00
    'ddd Do MMM YYYY HH:mm',
    // Mon 16th Jun 2025 18:00
    'ddd Do MMM YYYY HH:mm',
    // Outros formatos possíveis
    'DD/MM/YYYY HH:mm',
    'MM/DD/YYYY HH:mm',
    'YYYY/MM/DD HH:mm'
  ];
  
  let parsedDate;
  
  // Tentar cada formato até encontrar um que funcione
  for (const format of formats) {
    parsedDate = dayjs(cleanDate, format, true);
    if (parsedDate.isValid()) {
      break;
    }
  }
  
  // Se nenhum formato funcionou, tentar parsing automático
  if (!parsedDate || !parsedDate.isValid()) {
    parsedDate = dayjs(cleanDate);
  }
  
  // Se ainda não conseguiu parsear, retornar a string original
  if (!parsedDate.isValid()) {
    console.warn('Não foi possível parsear a data:', cleanDate);
    return cleanDate;
  }
  
  // Retornar no formato desejado: 24 Jun 2025 12:00
  return parsedDate.format('DD MMM YYYY HH:mm');
};


const cutOffColumns = [
  { 
    title: 'Tipo', 
    dataIndex: 'type', 
    key: 'type',
    render: (_: any, record: any) => (
      <Text>{getSiglaFullName(record.type)}</Text>
    )
  },
  { 
    title: 'Data', 
    dataIndex: 'date', 
    key: 'date',
    render: (_: any, record: any) => (
      <Text>{formatCutOffDate(record.date)}</Text>
    )
  },
];


// Função para obter dados de cut-offs
const getCutOffData = (cutOffs: any) => {
  return Object.entries(cutOffs).map(([key, value]) => ({
    type: key,
    date: value || 'N/A',
  }));
};

const MaritimeFreightSearchList: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [portsOrigin, setPortsOrigin] = useState<Port[]>([]);
  const [portsDestination, setPortsDestination] = useState<Port[]>([]);
  const [searchingCarrier, setSearchingCarrier] = useState<string>('');
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [selectedCutOffs, setSelectedCutOffs] = useState<any>(null);
  const navigate = useNavigate(); // Hook para navegação

  // Estados para os filtros
  const [filterCarrier, setFilterCarrier] = useState<string[]>([]);
  const [filterEtdRange, setFilterEtdRange] = useState<[moment.Moment | null, moment.Moment | null]>([null, null]);
  const [filterEtaRange, setFilterEtaRange] = useState<[moment.Moment | null, moment.Moment | null]>([null, null]);
  const [filterTransitTime, setFilterTransitTime] = useState<string>('');
  const [filterService, setFilterService] = useState<string>('');
  const [filterVessel, setFilterVessel] = useState<string>('');
  const [filterVoy, setFilterVoy] = useState<string>('');

  // Carrega portos de origem e destino
  useEffect(() => {
    const fetchPorts = async () => {
      try {
        const [originRes, destRes] = await Promise.all([
          api.get('filters/portos_embarque'),
          api.get('filters/portos_descarga'),
        ]);
        setPortsOrigin(originRes.data);
        setPortsDestination(destRes.data);
      } catch (error) {
        console.error('Erro ao carregar portos:', error);
      }
    };
    fetchPorts();
  }, []);

  // Transformações de dados para cada carrier
  const transformHapagData = useCallback((data: any) => {
    return data.data.routes.map((route: any) => ({
      carrier: 'HAPAG',
      service: route.legs[0].serviceName || 'N/A',
      vessel: route.legs[0].vesselDetails.name || 'N/A',
      voy: route.legs[0].scheduleVoyageNumber || 'N/A',
      etd: moment(route.placeOfReceiptDateTime).format('DD MMM YYYY'),
      eta: moment(route.placeOfDeliveryDateTime).format('DD MMM YYYY'),
      transitTime: `${route.transitTimeInDays} days`,
      cutOffs: route.gateInCutOffDateTimes.reduce((acc: any, cutOff: any) => {
        acc[cutOff.cutOffDateTimeCode] = moment(cutOff.cutOffDateTime).format('DD MMM YYYY HH:mm');
        return acc;
      }, {}), // Armazena os cut-offs para exibição no modal
    }));
  }, []);

  const transformMSCData = useCallback((data: any) => {
    return data.Data.flatMap((item: any) =>
      item.Routes.map((route: any) => ({
        carrier: 'MSC',
        service: item.Key.MaritimeServiceName,
        vessel: route.VesselName,
        voy: route.DepartureVoyageNo,
        etd: moment(route.EstimatedDepartureDate).format('DD MMM YYYY'),
        eta: moment(route.EstimatedArrivalDate).format('DD MMM YYYY'),
        transitTime: route.TotalTransitTime,
        cutOffs: route.CutOffs || {}, // Armazena os cut-offs para exibição no modal
      }))
    );
  }, []);

  const transformCMAData = useCallback((data: any) => {
    return data.map((item: any) => ({
      carrier: 'CMA',
      service: item['service-name'],
      vessel: item['vessel-name'],
      voy: item['voyage-ref'],
      etd: moment(item['departure-date']).format('DD MMM YYYY'),
      eta: moment(item['arrival-date']).format('DD MMM YYYY'),
      transitTime: `${item['transit-time']} days`,
      cutOffs: {
        specialBookingCutOff: item['special-booking-cut-off'],
        standardBookingCutOff: item['standard-booking-cut-off'],
        siCutOff: item['si-cut-off'],
        vgmCutOff: item['vgm-cut-off'],
        portCutOff: item['port-cut-off'],
      }, // Armazena os cut-offs para exibição no modal
    }));
  }, []);

  const transformONEData = useCallback((data: any) => {
    return data.scheduleLines.map((line: any) => ({
      carrier: 'ONE',
      service: line.sailInfo[0].serviceLaneName,
      vessel: line.sailInfo[0].vsslName,
      voy: line.sailInfo[0].vvd,
      etd: moment(line.polDepartureDateTime).format('DD MMM YYYY'),
      eta: moment(line.podArrivalDateTime || line.delArrivalDate).format('DD MMM YYYY'),
      transitTime: line.displayTransitDays ? `${line.displayTransitDays} days` : line.totalTransitTime,
      cutOffs: {
        cct: line.cct,
        dct: line.dct,
        vgmCct: line.vgmCct,
        inlandCct: line.inlandCct,
      }, // Armazena os cut-offs para exibição no modal
    }));
  }, []);

  const transformMaerskData = useCallback((data: any) => {
    return data.routings.flatMap((routing: any) => 
      routing.routingLegs.map((leg: any) => ({
        carrier: 'MAERSK',
        service: leg.carriage.vesselPortCallEnd.arrivalService.serviceName || 'N/A',
        vessel: leg.carriage.vessel.vesselName || 'N/A',
        voy: leg.carriage.vesselPortCallEnd.arrivalVoyageNumber || 'N/A',
        etd: moment(leg.carriage.vesselPortCallStart.estimatedTimeOfDeparture).format('DD MMM YYYY'),
        eta: moment(leg.carriage.vesselPortCallEnd.estimatedTimeOfArrival).format('DD MMM YYYY'),
        transitTime: moment.duration(leg.estimatedTransitTime).humanize(), // Converte o tempo de trânsito
        cutOffs: leg.deadlines.deadlines.reduce((acc: any, deadline: any) => {
          acc[deadline.deadlineCode] = moment(deadline.deadline).format('DD MMM YYYY HH:mm');
          return acc;
        }, {}), // Armazena os cut-offs para exibição no modal
      }))
    );
  }, []);

  // Função para buscar dados de um carrier por vez
  const fetchCarrierData = useCallback(
    async (carrier: string, formData: FormData) => {
      setSearchingCarrier(carrier);
      try {
        let response;
        const payload = {
          originCode: formData.originCode,
          destinationCode: formData.destinationCode,
          fromDate: formData.fromDate?.format('YYYY-MM-DD') || moment().format('YYYY-MM-DD'),
        };

        if (carrier === 'MSC') {
          response = await axios.post('https://msc.zarpar-beta.net/search', payload);
          const transformed = transformMSCData(response.data);
          setSchedules((prev) => {
            const newItems = transformed.filter(
              (newItem) =>
                !prev.some(
                  (existingItem) =>
                    existingItem.carrier === newItem.carrier &&
                    existingItem.voy === newItem.voy &&
                    existingItem.etd === newItem.etd &&
                    existingItem.eta === newItem.eta
                )
            );
            return [...prev, ...newItems];
          });
        } else if (carrier === 'CMA') {
          response = await axios.post('https://cma.zarpar-beta.net/search', payload);
          const transformed = transformCMAData(response.data);
          setSchedules((prev) => {
            const newItems = transformed.filter(
              (newItem) =>
                !prev.some(
                  (existingItem) =>
                    existingItem.carrier === newItem.carrier &&
                    existingItem.voy === newItem.voy &&
                    existingItem.etd === newItem.etd &&
                    existingItem.eta === newItem.eta
                )
            );
            return [...prev, ...newItems];
          });
        } else if (carrier === 'ONE') {
          const toDate = formData.fromDate
            ? moment(formData.fromDate).endOf('month').format('YYYY-MM-DD')
            : moment().endOf('month').format('YYYY-MM-DD');
          const url = `https://ecomm.one-line.com/api/v1/schedule/point-to-point?porCode=${payload.originCode}&delCode=${payload.destinationCode}&rcvTermCode=Y&deTermCode=Y&tsFlag=&fromDate=${payload.fromDate}&toDate=${toDate}&polCode=&podCode=&polYardCode=&podYardCode=&standardizationEtaEtb=true&cargoNature=GP&searchType=Calendar`;
          response = await axios.get(url);
          const transformed = transformONEData(response.data);
          setSchedules((prev) => {
            const newItems = transformed.filter(
              (newItem) =>
                !prev.some(
                  (existingItem) =>
                    existingItem.carrier === newItem.carrier &&
                    existingItem.voy === newItem.voy &&
                    existingItem.etd === newItem.etd &&
                    existingItem.eta === newItem.eta
                )
            );
            return [...prev, ...newItems];
          });
        } else if (carrier === 'HAPAG') {
          response = await axios.post('https://hapag.zarpar-beta.net/search', payload);
          const transformed = transformHapagData(response.data);
          setSchedules((prev) => {
            const newItems = transformed.filter(
              (newItem) =>
                !prev.some(
                  (existingItem) =>
                    existingItem.carrier === newItem.carrier &&
                    existingItem.voy === newItem.voy &&
                    existingItem.etd === newItem.etd &&
                    existingItem.eta === newItem.eta
                )
            );
            return [...prev, ...newItems];
          });
        } else if (carrier === 'MAERSK') {
          response = await axios.post('https://maersk.zarpar-beta.net/schedules', payload);
          const transformed = transformMaerskData(response.data);
          setSchedules((prev) => {
            const newItems = transformed.filter(
              (newItem) =>
                !prev.some(
                  (existingItem) =>
                    existingItem.carrier === newItem.carrier &&
                    existingItem.voy === newItem.voy &&
                    existingItem.etd === newItem.etd &&
                    existingItem.eta === newItem.eta
                )
            );
            return [...prev, ...newItems];
          });
        }
      } catch (error) {
        console.error(`Erro ao buscar dados do carrier ${carrier}:`, error);
      } finally {
        setSearchingCarrier('');
      }
    },
    [transformMSCData, transformCMAData, transformONEData, transformHapagData, transformMaerskData]
  );

  // Função de busca sequencial com efeito Trivago
  const onSearch = async (values: FormData) => {
    setLoading(true);
    setSchedules([]); // Limpa a lista antes de uma nova busca
    const carriers = ['MSC', 'CMA', 'ONE', 'HAPAG', 'MAERSK'];
    for (const carrier of carriers) {
      await fetchCarrierData(carrier, values);
      // Pequeno delay para simular efeito Trivago
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    setLoading(false);
  };

  // Função para lidar com as ações dos botões
  const handleActionClick = (action: string, record: Schedule) => {
    console.log(`Ação selecionada: ${action} para o registro:`, record);
    switch (action) {
      case 'booking':
        // Redireciona para o componente AddBooking com os dados necessários
        navigate('/bookingAdd', {
          state: {
            armador: record.carrier,
            portoEmbarque: form.getFieldValue('originCode'), // Porto de embarque selecionado
            portoDescarga: form.getFieldValue('destinationCode'), // Porto de destino selecionado
          },
        });
        break;
      case 'getQuotes':
        navigate('/quotes-enginner');
        break;
      case 'viewDeadlines':
        setSelectedCutOffs(record.cutOffs || {});
        setModalVisible(true);
        break;
      default:
        break;
    }
  };

  // Função para fechar o modal
  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedCutOffs(null);
  };

  // Colunas da tabela
  const columns = [
    {
      title: 'Carrier',
      dataIndex: 'carrier',
      key: 'carrier',
      render: (text: string) => (
        <Image src={CARRIER_LOGOS[text as keyof typeof CARRIER_LOGOS]} width={50} preview={false} />
      ),
    },
    { title: 'Service', dataIndex: 'service', key: 'service' },
    { title: 'Vessel', dataIndex: 'vessel', key: 'vessel' },
    { title: 'Voy', dataIndex: 'voy', key: 'voy' },
    {
      title: 'ETD',
      dataIndex: 'etd',
      key: 'etd',
      sorter: (a: Schedule, b: Schedule) => moment(a.etd, 'DD MMM YYYY').unix() - moment(b.etd, 'DD MMM YYYY').unix(),
    },
    { title: 'ETA', dataIndex: 'eta', key: 'eta' },
    { title: 'T/T', dataIndex: 'transitTime', key: 'transitTime' },
    {
      title: 'Ações',
      key: 'actions',
      render: (_: any, record: Schedule) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            type="primary"
            icon={<BookOutlined style={{ color: 'white' }} />}
            onClick={() => handleActionClick('booking', record)}
          >
            Booking
          </Button>
          <Button
            size="small"
            type='primary'
            icon={<DollarOutlined style={{ color: 'white' }} />}
            onClick={() => handleActionClick('getQuotes', record)}
          >
            Get Quotes
          </Button>
          <Button
            size="small"
            type="dashed"
            icon={<ClockCircleOutlined style={{ color: '#1677ff' }} />}
            onClick={() => handleActionClick('viewDeadlines', record)}
          >
           Cut-off
          </Button>
        </div>
      ),
    },
  ];

  // Filtrar os dados com base nos filtros selecionados
  const filteredSchedules = schedules.filter((schedule) => {
    // Filtro de Carrier
    if (filterCarrier.length > 0 && !filterCarrier.includes(schedule.carrier)) {
      return false;
    }

    // Filtro de ETD Range
    if (filterEtdRange[0] && filterEtdRange[1]) {
      const etdDate = moment(schedule.etd, 'DD MMM YYYY');
      if (!etdDate.isBetween(filterEtdRange[0], filterEtdRange[1], 'day', '[]')) {
        return false;
      }
    }

    // Filtro de ETA Range
    if (filterEtaRange[0] && filterEtaRange[1]) {
      const etaDate = moment(schedule.eta, 'DD MMM YYYY');
      if (!etaDate.isBetween(filterEtaRange[0], filterEtaRange[1], 'day', '[]')) {
        return false;
      }
    }

    // Filtro de Transit Time (T/T)
    if (filterTransitTime && schedule.transitTime !== filterTransitTime) {
      return false;
    }

       // Filtro de Service
    if (filterService && schedule.service !== filterService) {
      return false;
    }

    // Filtro de Vessel
    if (filterVessel && schedule.vessel !== filterVessel) {
      return false;
    }

    // Filtro de Voyage (Voy)
    if (filterVoy && schedule.voy !== filterVoy) {
      return false;
    }

    return true;
  });

  // Obter opções únicas para os filtros de Select
  const uniqueVessels = Array.from(new Set(schedules.map((s) => s.vessel))).sort();
  const uniqueVoys = Array.from(new Set(schedules.map((s) => s.voy))).sort();
  const uniqueTransitTimes = Array.from(new Set(schedules.map((s) => s.transitTime))).sort();
  const uniqueServices = Array.from(new Set(schedules.map((s) => s.service))).sort();

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <Title
          level={2}
          style={{
            borderLeft: '5px solid #1677ff',
            paddingLeft: '15px',
            margin: 0,
            lineHeight: '1.5',
          }}
        >
          Encontre os melhores serviços, programações de navios por armador
        </Title>
      </motion.div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeInUpDelayed}
        style={{ marginBottom: '20px', marginTop: '10px' }}
      >
        <Text style={{ display: 'block' }}>
          Explore rotas, horários e programações de navios por armador. Compare serviços, encontre as melhores opções e contrate fretes ou bookings diretamente, tudo em uma plataforma integrada e prática!
        </Text>
      </motion.div>

      <Card title="Buscar" style={{ marginBottom: '20px', marginTop: '40px' }}>
        <Form form={form} layout="inline" onFinish={onSearch}>
          <Form.Item
            name="originCode"
            label="Porto de Embarque"
            rules={[{ required: true, message: 'Selecione o porto de embarque' }]}
          >
            <Select
              placeholder="Selecione o porto"
              style={{ width: 200 }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {portsOrigin.map((port) => (
                <Option key={port._id} value={port.port_code}>
                  {port.port_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="destinationCode"
            label="Porto de Destino"
            rules={[{ required: true, message: 'Selecione o porto de destino' }]}
          >
            <Select
              placeholder="Selecione o porto"
              style={{ width: 200 }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {portsDestination.map((port) => (
                <Option key={port._id} value={port.port_code}>
                  {port.port_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="fromDate"
            label="Data"
            rules={[{ required: true, message: 'Selecione a data' }]}
          >
            <DatePicker format="YYYY-MM-DD" style={{ width: 150 }} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading}>
              Buscar
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {loading && (
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Spin tip={`Buscando dados de ${searchingCarrier || 'carriers'}...`} />
        </div>
      )}

      {!loading && schedules.length > 0 && (
        <Card title="Filtros" style={{ marginBottom: '20px' }}>
          <Text style={{ display: 'block', marginBottom: '16px' }}>
            Utilize os filtros abaixo para refinar os resultados da busca. Selecione carriers, intervalos de datas para ETD e ETA, tempo de trânsito, serviço, navio ou voyage para encontrar as opções mais adequadas às suas necessidades.
          </Text>
          <Form layout="vertical">
            <div className="row g-3"> {/* 'g-3' para espaçamento entre colunas e linhas */}
              <div className="col-12 col-md-6 col-lg-4"> {/* Responsivo: 1 coluna em mobile, 2 em tablet, 3 em desktop */}
                <Form.Item 
                  label="Carrier" 
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    mode="multiple"
                    placeholder="Selecione carriers"
                    style={{ width: '100%' }}
                    value={filterCarrier}
                    onChange={(value) => setFilterCarrier(value)}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {['MSC', 'CMA', 'ONE', 'HAPAG', 'MAERSK'].map((carrier) => (
                      <Option key={carrier} value={carrier}>
                        {carrier}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Item 
                  label="ETD Range" 
                  style={{ marginBottom: 0 }}
                >
                  <DatePicker.RangePicker
                    format="YYYY-MM-DD"
                    style={{ width: '100%' }}
                    value={filterEtdRange}
                    onChange={(dates) => setFilterEtdRange(dates as [moment.Moment | null, moment.Moment | null])}
                    allowClear
                  />
                </Form.Item>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Item 
                  label="ETA Range" 
                  style={{ marginBottom: 0 }}
                >
                  <DatePicker.RangePicker
                    format="YYYY-MM-DD"
                    style={{ width: '100%' }}
                    value={filterEtaRange}
                    onChange={(dates) => setFilterEtaRange(dates as [moment.Moment | null, moment.Moment | null])}
                    allowClear
                  />
                </Form.Item>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Item 
                  label="Transit Time (T/T)" 
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Selecione o tempo de trânsito"
                    style={{ width: '100%' }}
                    value={filterTransitTime || undefined}
                    onChange={(value) => setFilterTransitTime(value || '')}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {uniqueTransitTimes.map((tt) => (
                      <Option key={tt} value={tt}>
                        {tt}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Item 
                  label="Service" 
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Selecione o serviço"
                    style={{ width: '100%' }}
                    value={filterService || undefined}
                    onChange={(value) => setFilterService(value || '')}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {uniqueServices.map((service) => (
                      <Option key={service} value={service}>
                        {service}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Item 
                  label="Vessel" 
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Selecione o navio"
                    style={{ width: '100%' }}
                    value={filterVessel || undefined}
                    onChange={(value) => setFilterVessel(value || '')}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {uniqueVessels.map((vessel) => (
                      <Option key={vessel} value={vessel}>
                        {vessel}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Item 
                  label="Voyage (Voy)" 
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Selecione o voyage"
                    style={{ width: '100%' }}
                    value={filterVoy || undefined}
                    onChange={(value) => setFilterVoy(value || '')}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {uniqueVoys.map((voy) => (
                      <Option key={voy} value={voy}>
                        {voy}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
            </div>
          </Form>
        </Card>
      )}

      <Table
        dataSource={filteredSchedules.sort((a, b) => moment(a.etd, 'DD MMM YYYY').unix() - moment(b.etd, 'DD MMM YYYY').unix())}
        columns={columns}
        rowKey={(record) => `${record.carrier}-${record.voy}-${record.etd}-${record.eta}-${Math.random()}`}
        loading={loading}
        pagination={{ pageSize: 100 }}
      />

      <Modal
        title="Cut-Off Deadlines"
        visible={modalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            Fechar
          </Button>,
        ]}
        width={600}
      >
        {selectedCutOffs ? (
          <div style={{ padding: '10px 0' }}>
            {Object.keys(selectedCutOffs).length > 0 ? (
              <Table
                dataSource={getCutOffData(selectedCutOffs)}
                columns={cutOffColumns}
                pagination={false}
                rowKey={(record) => record.type}
                size="small"
              />
            ) : (
              <Text>Nenhum dado de cut-off disponível para este registro.</Text>
            )}
          </div>
        ) : (
          <Text>Carregando dados de cut-off...</Text>
        )}
      </Modal>
    </div>
  );
};

export default MaritimeFreightSearchList;
